import { useState } from 'react';
import { DEFENSE_MODES } from '../../lib/batting-order';
import { WizardBar, WizardHead, WizardFoot, WizardList, WizardSelect, TwoPane } from './WizardChrome';
import { DefenseGrid, RebuildDefenseSheet } from './DefenseGrid';
import { BarChart3, ChevronRight, AlertTriangle } from 'lucide-react';

// Step 2. The whole game as one grid (see DefenseGrid): players down the side
// in batting order, innings across, a position in every cell, so the inning
// before and the inning after stay in view while the coach changes one. (An
// earlier version showed one inning at a time behind inning tabs; coaches lost
// track of where a kid had just played.)

export const StepDefense = ({
  innings, numInnings, focusInning, onCopyToLaterInnings, onAddInning, onRemoveInning, defenseEdited,
  players, stepLabel, defenseMode, onDefenseMode, isGenerating,
  fairnessNote, lightCount, selectedPitcherId, settings,
  onReplacePitcher, onFieldTime, onSetPosition, onBack, onContinue, onSkip, wide = false, side,
}: {
  innings: any[];
  numInnings: number;
  /** Inning to bring into view on arrival (0-based), e.g. one tapped on the review card. */
  focusInning: number;
  onCopyToLaterInnings: (inningIdx: number) => void;
  /** Absent when the plan is already at its longest / shortest. */
  onAddInning?: () => void;
  onRemoveInning?: () => void;
  defenseEdited: boolean;
  players: any[];
  stepLabel: string;
  defenseMode: string;
  onDefenseMode: (mode: string) => void;
  isGenerating: boolean;
  fairnessNote: string | null;
  lightCount: number;
  selectedPitcherId: string | null;
  settings: any;
  onReplacePitcher: () => void;
  onFieldTime: () => void;
  onSetPosition: (inningIdx: number, playerId: string, position: string) => void;
  onBack: () => void;
  onContinue: () => void;
  onSkip: () => void;
  /** Wide screens: the grid becomes the Lineup Wall and `side` fills the right pane. */
  wide?: boolean;
  side?: React.ReactNode;
}) => {
  // A fill mode picked after hand edits waits here for the coach to confirm,
  // because every mode rebuilds all innings.
  const [pendingMode, setPendingMode] = useState<string | null>(null);
  const handleMode = (mode: string) => {
    if (defenseEdited && mode !== defenseMode) setPendingMode(mode);
    else onDefenseMode(mode);
  };

  const hasDefense = innings.slice(0, numInnings).some(inn => inn?.length > 0);
  const missingPitcher = hasDefense && !settings?.noPitcher
    && !(innings[0] || []).some((e: any) => e.position === 'P');

  return (
    <TwoPane wide={wide} side={side}>
      <WizardBar label={stepLabel} onBack={onBack} />
      <WizardHead
        title="Defensive Positions"
        sub={fairnessNote ?? 'Every inning at a glance. Tap a position to change it.'}
      />

      <div className="flex-1 overflow-y-auto bg-canvas px-4 pt-4">
        <div className="mb-4">
          <WizardSelect
            label="Fill defense by"
            value={defenseMode}
            options={DEFENSE_MODES.map(m => ({ key: m.key, label: m.label, description: m.description }))}
            onChange={handleMode}
          />
        </div>

        {missingPitcher && (
          <button
            onClick={onReplacePitcher}
            className="w-full flex items-center gap-2.5 bg-gold-soft rounded-card px-4 py-3 mb-4 text-left"
          >
            <AlertTriangle className="w-4 h-4 text-gold-ink shrink-0" />
            <span className="flex-1 text-[13.5px] font-semibold text-gold-ink">
              No starting pitcher chosen, so P is empty.
            </span>
            <ChevronRight className="w-4 h-4 text-gold-ink shrink-0" />
          </button>
        )}

        {isGenerating && (
          <p className="text-sm text-ink-2 px-1 py-6 text-center">Working out the defense…</p>
        )}

        {!isGenerating && !hasDefense && (
          <p className="text-sm text-ink-2 px-1 py-6 text-center">
            Pick a fill mode above to build a defense.
          </p>
        )}

        {!isGenerating && hasDefense && (
          <DefenseGrid
            innings={innings}
            numInnings={numInnings}
            players={players}
            settings={settings}
            focusInning={focusInning}
            onSetPosition={onSetPosition}
            onCopyToLaterInnings={onCopyToLaterInnings}
            onAddInning={onAddInning}
            onRemoveInning={onRemoveInning}
            wide={wide}
          />
        )}

        {/* Advisory. Nothing here changes the lineup; it informs the coach. */}
        <div className="mt-4">
          <WizardList>
            <button onClick={onFieldTime} className="w-full flex items-center gap-3 px-4 py-3.5 text-left">
              <span className="w-[30px] h-[30px] shrink-0 rounded-control bg-raised flex items-center justify-center">
                <BarChart3 className="w-[17px] h-[17px] text-ink" />
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-base font-semibold text-ink">Season field time</span>
                <span className="block text-[13px] text-ink-2 mt-0.5">
                  {lightCount > 0
                    ? `${lightCount} player${lightCount === 1 ? ' is' : 's are'} running light`
                    : 'Playing time is even so far'}
                </span>
              </span>
              <ChevronRight className="w-5 h-5 text-ink-mute shrink-0" />
            </button>
          </WizardList>
        </div>

        <div className="h-4" />
      </div>

      <WizardFoot
        primaryLabel="Continue"
        onPrimary={onContinue}
        secondaryLabel="Skip this step"
        onSecondary={onSkip}
      />

      <RebuildDefenseSheet
        mode={pendingMode}
        onConfirm={(m) => { setPendingMode(null); onDefenseMode(m); }}
        onCancel={() => setPendingMode(null)}
      />
    </TwoPane>
  );
};
